import React, { useState, useEffect } from "react";
import "./WorkloadDetailModal.css";
import { useNavigate } from "react-router-dom";
import { fetchUserWorkloadData } from "./Workloadapi";
import { X, Clock, Layers, ChevronDown } from "lucide-react";
import moment from "moment";
import AssignTicketModal from "./AssignTicketModal";
import ProfileIcon from "../../Assets/png/user-icon.png";

const WorkloadDetailModal = ({ member, date, onClose, onSuccess }) => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState("All");
  const [selectedProject, setSelectedProject] = useState("All");
  const [loading, setLoading] = useState(true);
  const [workloadData, setWorkloadData] = useState(null);
  const [priorityFilter, setPriorityFilter] = useState("All Priority");
  const [showPriorityDropdown, setShowPriorityDropdown] = useState(false);
  const [selectedTicketToAssign, setSelectedTicketToAssign] = useState(null);
  const [refreshTrigger, setRefreshTrigger] = useState(0);

  useEffect(() => {
    const fetchUserDetails = async () => {
      try {
        setLoading(true);
        const response = await fetchUserWorkloadData(member.id, date);

        if (response && response.data && response.data.success) {
          setWorkloadData(response.data.data);
        } else {
          console.error("API Error:", response?.data?.message || "Unknown error");
        }
      } catch (error) {
        console.error("Network or other error fetching user workload details", error);
      } finally {
        setLoading(false);
      }
    };

    if (member && date) {
      fetchUserDetails();
    }
  }, [member, date, refreshTrigger]);

  const employee = workloadData?.employee || member;
  const projectHours = workloadData?.project_hours || [];
  const tickets = workloadData?.tickets || [];
  const tabs = workloadData?.tabs || { all: 0, open: 0, hold: 0 };
  const summary = workloadData?.summary || { total_tickets: 0, total_hours: '00:00 hrs' };
  
  // Base filtering by Project AND Priority
  const baseFiltered = tickets.filter(t => {
    const matchesProject = selectedProject === "All" || t.project === selectedProject;
    const matchesPriority = priorityFilter === "All Priority" || t.priority?.toLowerCase() === priorityFilter.toLowerCase();
    return matchesProject && matchesPriority;
  });

  const tabCounts = {
    All: baseFiltered.length,
    open: baseFiltered.filter(t => t.status?.toLowerCase() === "open").length,
    "in progress": baseFiltered.filter(t => t.status?.toLowerCase() === "in progress").length,
    hold: baseFiltered.filter(t => t.status?.toLowerCase() === "hold").length
  };

  // Final filtered list based on Tab
  const filteredTickets = baseFiltered.filter(ticket => {
    if (activeTab === "All") return true;
    return ticket.status?.toLowerCase() === activeTab.toLowerCase();
  });

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="workload-detail-modal" onClick={(e) => e.stopPropagation()}>
        {/* Header Section */}
        <div className="modal-header-section">
          <button className="close-modal-btn" onClick={onClose}>
            <X size={20} />
          </button>
          
          <div className="member-profile-header">
            <img src={employee?.profile_image || ProfileIcon} alt="Profile" className="profile-avatar" />
            <div className="profile-info">
              <h3 className="member-name">{employee?.name || "-"}</h3>
              <p className="member-role">{employee?.designation || "-"}</p>
            </div>
          </div>

          <div className="summary-stats">
            <span className="stat-date">{workloadData?.workload_date || moment(date).format("D MMM YYYY")}</span>
            <span className="stat-dot">•</span>
            <span className="stat-tickets">{summary.total_tickets} tickets</span>
            <span className="stat-dot">•</span>
            <span className="stat-hours">{summary.total_hours}</span>
          </div>
        </div>

        {/* Body Section */}
        <div className="modal-body-section">
          {loading ? (
            <div className="loading-container">Loading details...</div>
          ) : (
            <>
              {/* Left Sidebar */}
              <div className="project-hours-sidebar">
                <h4 className="sidebar-title">Project Hours</h4>
                <div className="project-list">
                  <div 
                    className={`project-item ${selectedProject === "All" ? "active" : ""}`}
                    onClick={() => setSelectedProject("All")}
                  >
                    <span className="project-name">All Projects</span>
                    <span className="project-hours">({summary.total_hours})</span>
                  </div>
                  {projectHours.map((proj, idx) => (
                    <div 
                      key={idx} 
                      className={`project-item ${selectedProject === proj.project_name ? "active" : ""}`}
                      onClick={() => setSelectedProject(proj.project_name)}
                    >
                      <span className="project-name">{proj.project_name}</span>
                      <span className="project-hours">({proj.hours})</span>
                    </div>
                  ))}
                  {projectHours.length === 0 && <div className="no-data">No project data</div>}
                </div>
              </div>

              {/* Right Content - Tickets */}
              <div className="tickets-main-content">
                <div className="tickets-header-controls">
                  <div className="status-tabs">
                    <button 
                      className={`tab-btn ${activeTab === "All" ? "active" : ""}`}
                      onClick={() => setActiveTab("All")}
                    >
                      All ({tabCounts.All})
                    </button>
                    <button 
                      className={`tab-btn ${activeTab === "open" ? "active" : ""}`}
                      onClick={() => setActiveTab("open")}
                    >
                      Open ({tabCounts.open})
                    </button>
                    <button 
                      className={`tab-btn ${activeTab === "in progress" ? "active" : ""}`}
                      onClick={() => setActiveTab("in progress")}
                    >
                      In Progress ({tabCounts["in progress"]})
                    </button>
                    <button 
                      className={`tab-btn ${activeTab === "hold" ? "active" : ""}`}
                      onClick={() => setActiveTab("hold")}
                    >
                      Hold ({tabCounts.hold})
                    </button>
                  </div>

                  <div className="priority-filter-container">
                    <div 
                      className="priority-selector" 
                      onClick={() => setShowPriorityDropdown(!showPriorityDropdown)}
                    >
                      <span className="priority-label">{priorityFilter}</span>
                      <ChevronDown size={16} />
                    </div>
                    {showPriorityDropdown && (
                      <div className="priority-dropdown-menu">
                        {["All Priority", "Low", "Medium", "High"].map(p => (
                          <div 
                            key={p} 
                            className={`priority-option ${priorityFilter === p ? "active" : ""}`}
                            onClick={() => {
                              setPriorityFilter(p);
                              setShowPriorityDropdown(false);
                            }}
                          >
                            {p}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>

                <div className="tickets-list">
                  {filteredTickets.map((ticket, idx) => (
                    <div 
                      key={idx} 
                      className="ticket-card" 
                      onClick={() => navigate(`/ticket/view/${ticket.id}`)}
                      style={{ cursor: 'pointer' }}
                    >
                      <div className="ticket-icon-wrapper">
                        <Layers size={20} />
                      </div>
                      <div className="ticket-details">
                        <div className="ticket-title-row">
                          <h5 className="ticket-title">{ticket.title}</h5>
                        </div>
                        <div className="ticket-meta-row">
                          <span>{ticket.project}</span>
                          <span className="stat-dot">•</span>
                          <span className="meta-status">{ticket.status}</span>
                          <span className="stat-dot">•</span>
                          <span className={`priority-badge priority-${(ticket.priority || 'Low').toLowerCase()}`}>
                            {ticket.priority || 'Low'}
                          </span>
                        </div>
                        <div className="ticket-estimation">
                          <Clock size={14} className="clock-icon" />
                          <span>Estimation : {ticket.estimation}</span>
                        </div>
                      </div>
                      {['open', 'in progress', 'hold'].includes(ticket.status?.toLowerCase()) && (
                        <button className="assign-btn" onClick={(e) => { e.stopPropagation(); setSelectedTicketToAssign(ticket); }}>Assign</button>
                      )}
                    </div>
                  ))}
                  {filteredTickets.length === 0 && (
                    <div className="no-tickets">No tickets found for this status.</div>
                  )}
                </div>
              </div>
            </>
          )}
        </div>
      </div>

      {selectedTicketToAssign && (
        <AssignTicketModal
          ticket={selectedTicketToAssign}
          onClose={() => setSelectedTicketToAssign(null)}
          onSuccess={() => {
            setRefreshTrigger(prev => prev + 1);
            if (onSuccess) onSuccess();
          }}
        />
      )}
    </div>
  );
};

export default WorkloadDetailModal;
